import { useLazyQuery } from '@apollo/client'
import { BestPracticeQuery } from 'api/graphQL/queries/bestPractices.queries'
import { GetProjectQuery } from 'api/graphQL/queries/project.queries'
import { useHoveredItemActions } from 'components/WorkspaceEditor/HoveredItemStore'

/**
 * Shared hook for Best Practices GraphQL queries.
 *
 * Centralizes the query logic for fetching Best Practice and Project data.
 * This eliminates code duplication and ensures consistent query behavior.
 *
 * @returns Object with getBestPracticeElementData and getProjectData query functions
 *
 * @example
 * const { getBestPracticeElementData, getProjectData } = useBestPracticesQueries()
 *
 * getBestPracticeElementData({
 *   variables: {
 *     parentId: contentItemId,
 *     projectId: projectId,
 *   }
 * })
 */
export const useBestPracticesQueries = () => {
    const {
        setBestPracticeElementData,
        setProjectData,
    } = useHoveredItemActions()

    // Fetch Best Practice data
    const [getBestPracticeElementData] = useLazyQuery(BestPracticeQuery, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            setBestPracticeElementData(data)
        },
    })

    // Fetch Project data
    const [getProjectData] = useLazyQuery(GetProjectQuery, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            setProjectData(data)
        },
    })

    return {
        getBestPracticeElementData,
        getProjectData,
    }
}
